import { MessageSquare, BookMarked, Trash2 } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Right-hand sidebar listing all comments and close-reading annotations
 * for the active chapter, sorted by position. Replaces the inline gutter
 * CommentMarker icons with a persistent, scrollable panel.
 *
 * Props:
 *   selections:   full array of TextSelection-shaped objects (filtered internally)
 *   onScrollTo:   (selection) => void — scrolls the reader to that mark
 *   onDelete:     (selection) => void — deletes the selection
 */
export default function AnnotationSidebar({ selections, onScrollTo, onDelete }) {
  const annotations = (selections || [])
    .filter((s) => s.kind === 'comment' || s.kind === 'close_reading')
    .sort((a, b) => (a.text_start ?? 0) - (b.text_start ?? 0));

  if (annotations.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-gray-400 dark:text-gray-500">
        <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-40" strokeWidth={1.5} />
        <p>No annotations yet.</p>
        <p className="text-xs mt-1">Select text and click Comment or Close Reading.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-800">
      {annotations.map((sel) => {
        const Icon = sel.kind === 'close_reading' ? BookMarked : MessageSquare;
        return (
          <div
            key={sel.id}
            className="group px-3 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
            onClick={() => onScrollTo?.(sel)}
          >
            <div className="flex items-start gap-2">
              <Icon
                className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400 dark:text-gray-500"
                strokeWidth={1.5}
              />
              <div className="min-w-0 flex-1">
                {sel.title && (
                  <p className="text-xs font-semibold text-gray-900 dark:text-white mb-0.5">
                    {sel.title}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 italic line-clamp-2">
                  &ldquo;{(sel.selected_text || '').slice(0, 80)}&rdquo;
                </p>
                {sel.note && (
                  <p className="mt-1 text-xs text-gray-700 dark:text-gray-300 line-clamp-3">
                    {sel.note}
                  </p>
                )}
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {formatDate(sel.created_at)}
                  </span>
                  {onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(sel);
                      }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-rose-500 dark:hover:text-rose-400"
                      title="Delete annotation"
                    >
                      <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
